import { EsfirusSelectPropsOption } from '@components/ui/Select/EsfirusSelect';
import { getInternalCompany } from '@helpers/companyData';
import { constants } from '@helpers/constants';
import { Activity, SubActivity } from '@models/activitiesAndSubActivities';
import { Capitulo } from '@models/obra';
import { UserWithDataParsed } from '@models/user';
import Grid from '@mui/material/Grid';
import { configurationService } from '@services/configApp';
import { useDispatch, useSelector } from '@services/hooks/redux-hooks';
import { calculateSeconds, calculateTime } from '@services/hooks/TimeHook.js';
import { useDebouncedCallback } from '@services/hooks/useDebounceCallback';
import useRefState from '@services/hooks/useRefState';
import {
  getCategoriasByCompany,
  getCosteEmpleado,
  getUserId,
} from '@services/hooks/UserHook';
import { reportLinesService } from '@services/reportLines';
import { updateLine } from '@store/slices/lines';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Categoria,
  Linea,
  LineaManoDeObraHookProps,
  LineaManoDeObraHookReturn,
  TipoHora,
} from './LaborLinesTypes';

const NO_MATCH_CATEGORY_TYPEHOUR = 'no-match-category-typeHour';

const useLaborLinesHook = (
  props: LineaManoDeObraHookProps
): LineaManoDeObraHookReturn => {
  const dispatch = useDispatch();
  const lineas = useSelector((state) => (state as any).lines);
  const categorias = useSelector((state) => (state as any).categorias).list;
  const tipoHoras = useSelector((state) => (state as any).tipoHora).list;
  const tipoHorasByEmpleado = useSelector(
    (state) => (state as any).tipoHora
  ).listByEmpleado;
  const selectedobra = useSelector((state) => (state as any).selectedobra).obra;
  const manoObrasList = useSelector((state) => (state as any).mano_obra).list;
  const calendarios = useSelector((state) => (state as any).calendarios).list;
  const empresas = useSelector((state) => (state as any).empresas).list;
  const users = useSelector((state) => (state as any).users.list);
  const obras = useSelector((state) => state.obras.list);
  const { activities, subActivities } = useSelector(
    (state) => (state as any).activitiesAndSubActivities
  );

  const [allLinesUser, setAllLinesUser] = useState<any>([]);
  const [loadingSP, setLoadingSP] = useState<boolean>(false);
  const [loaded, setLoaded] = useState(false);
  const [savedData, setSavedData] = useState<Record<string, number | string>>(
    {}
  );
  const [description, setDescription] = useState<string>(lineas.list[props.index]?.description ?? '');

  // Handling sobre el touched de los inputs de tiempos.
  const [touchedTimeFields, setTouchedTimeFields] = useState<boolean>(false);

  const handleBlurTimeInputs = () => {
    setTouchedTimeFields(true);
  };

  const debouncedChange = useDebouncedCallback((line: any) => {
    changeLineHandler(line);
  }, 500);

  useEffect(() => {
    debouncedChange({
      ...lineas.list[props.index],
      description,
      user: lineas?.list[props.index]?.userWorker?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description]);

  const {
    selectHoursMinutesManoObra,
    manoObra,
    manoObraTipoHora,
    manoObraTipoHoraDefault,
    descManoObra,
    showManoObraCategoria,
    showManoObraTipoHora,
    manoObraCategoria,
    manoObraCategoriaDefault,
    manoObraDiferenteDia,
  } = configurationService.getConfigLineasParte();

  // TODO: Esto podria ir a un HookDE Congif. pero hay que pasar todas los get para allà.
  // Dejo esto por acà para hacer a futuro
  const config = localStorage?.getItem('ef-configuration') || '';
  const config_JSON = JSON.parse(config);
  const config_calendarios = config_JSON?.calendarios ?? {};

  // Calendarios cuando tipo de hora es varios.
  const [calendariosEnTipoDeHoraVarios, setCalendariosEnTipoDeHoraVarios] =
    useRefState<any[]>([]);

  const empleadosOptions = useMemo(() => {
    const empleadosOfInternalCompany = users.filter((em: any) => {
      const isInInternalArray = em.data.internal.some(
        (v: any) => v.empresa == getInternalCompany()
      );
      return isInInternalArray;
    });

    const options = empleadosOfInternalCompany.map((empleado: any) => ({
      label: `${empleado.nombre} ${empleado.apellidos}`,
      value: empleado.id,
    }));

    options.sort((a: any, b: any) => a.label.localeCompare(b.label));

    return options;
  }, [users]);

  const allowToSelectEmpleado = useMemo(
    () =>
      config_JSON?.lineasParte?.manoObraSeleccionarEmpleado &&
      !config_JSON?.buscadorObra?.filtroObraShowEmpresa,
    [config_JSON]
  );

  const isEqual = (a: any, b: any) => {
    return JSON.stringify(a) === JSON.stringify(b);
  };

  const changeLineHandler = (changes: any) => {
    if (!isEqual(lineas.list[props.index], changes)) {
      dispatch(updateLine(changes));
    }
  };

  const getShowFoundSpecificTime = useCallback(
    (_line: any) => {
      const line = _line ?? lineas.list[props.index];

      // Si ya tiene un valor definido, lo devolvemos.
      if (line.hasOwnProperty('hoursAndMinutesInRaw')) {
        return Boolean(!line.hoursAndMinutesInRaw);
      } else if (line.hours > 0 || line.minutes > 0) {
        // Si no, miramos si tiene horas y minutos definidos.
        return false;
      } else if (line.numberHours) {
        // Si no, miramos si tiene numberHours definidos.
        return true;
      }

      if (manoObra === 'hora') return true;

      return !selectHoursMinutesManoObra;
    },
    [manoObra, selectHoursMinutesManoObra, lineas.list[props.index]]
  );

  useEffect(() => {
    setSavedData(lineas?.list[props.index]);
  }, []);

  useEffect(() => {
    fetchData();
  }, [lineas?.list[props.index]?.userWorker]);

  useEffect(() => {
    recalculate(lineas?.list[props.index]?.startTime, false);
  }, [selectedobra]);

  const getArticleManoObra = (
    categoriaValue: any,
    categoriaExternoValue: any,
    typeHourValue: any
  ) => {
    let articleValue: any = null;
    let articleExternoValue: any = null;

    const getting = (categoriaValue: any, typeHourValue: any, company: any) => {
      let articleValue = null;

      if (categoriaValue && typeHourValue) {
        const empresaSelected = empresas.find(
          (emp: any) => emp.Empresa == company
        );

        const manoObraSelected = manoObrasList.find(
          (c: any) =>
            c.Categoria == categoriaValue &&
            c.Tipo_Hora == typeHourValue &&
            c.Ambito == empresaSelected.Ambito
        );

        if (!manoObraSelected) {
          articleValue = NO_MATCH_CATEGORY_TYPEHOUR;
        } else {
          articleValue = manoObraSelected.Articulo;
        }
      }

      return articleValue;
    };

    if (
      categoriaValue &&
      typeHourValue &&
      manoObrasList &&
      manoObrasList.length > 0
    ) {
      if (typeHourValue == constants.tipo_hora_varios.TipoHora) {
        const dataGettingPromises = calendariosEnTipoDeHoraVarios.map(
          (calendario) => {
            const dataGetting = getting(
              categoriaValue,
              calendario.TipoHora,
              selectedobra.company
            );
            const dataGettingExterno = getting(
              categoriaExternoValue,
              calendario.TipoHora,
              selectedobra.companyDestino
            );
            return {
              calendario,
              article: dataGetting,
              articleExterno: dataGettingExterno,
            };
          }
        );
        articleValue = dataGettingPromises.map((result) => ({
          calendario: result.calendario,
          article: result.article,
        }));
        articleExternoValue = dataGettingPromises.map((result) => ({
          calendario: result.calendario,
          article: result.articleExterno,
        }));
      } else {
        articleValue = getting(
          categoriaValue,
          typeHourValue,
          selectedobra.company
        );
        articleExternoValue = getting(
          categoriaExternoValue,
          typeHourValue,
          selectedobra.companyDestino
        );
      }
    }

    return {
      article: articleValue,
      articleExterno: articleExternoValue,
    };
  };

  const updateData = async (resetByUserWorker?: UserWithDataParsed) => {
    const { list } = lineas ?? {};
    const lineItem = list?.[props.index] ?? {};

    const minutes = lineItem?.minutes ?? 0;
    const hours = lineItem?.hours ?? 0;
    const costeEmpleadoValue =
      lineItem?.costeEmpleado ??
      lineItem?.userWorker?.data?.coste ??
      getCosteEmpleado();
    const horasDecimal = getHoursDecimal();

    // Variable relacionada con el tipo de elección de horas: espcífica o en bruto
    let hoursAndMinutesInRaw: boolean = Boolean(
      !getShowFoundSpecificTime(lineItem)
    );

    // Variables relacionadas con usuario/trabajador
    let userWorkerValue: UserWithDataParsed | undefined =
      resetByUserWorker || lineItem?.userWorker;
    let allowParteFuturo: boolean = false;

    // Variables relacionadas con categorías
    let categoriaValue = lineItem?.category ?? null;
    let categoriaExternoValue = lineItem?.categoryExterno ?? null;

    // Variables relacionadas con tipo de hora y precios
    let typeHourValue = lineItem?.typeHour ?? null;
    let precioValue = lineItem?.precio ?? null;
    let precioExternoValue = lineItem?.precioExterno ?? null;

    // Variables relacionadas con artículos
    let articleValue = lineItem?.article ?? null;
    let articleExternoValue = lineItem?.articleExterno ?? null;

    // Variables relacionadas con ventas
    let newDataSales = lineItem?.data_sales_pricing ?? null;
    let newDataSalesExterno = lineItem?.data_sales_pricing_externo ?? null;

    //ponemos el empleado de la mano de obra por defecto
    if (!lineItem?.userWorker) {
      const userCabecera = users.find(
        (user: any) => user.id == (selectedobra?.userEmpleadoId ?? getUserId())
      );
      userWorkerValue = userCabecera;
    }

    //ponemos la categoria por defecto
    if (
      (categorias && categorias.length > 0 && !lineItem?.category) ||
      resetByUserWorker
    ) {
      const categoriasDelEmpleado = getCategoriasByCompany(userWorkerValue);
      const categoriaInterno =
        categoriasDelEmpleado.find((c) => c.empresa == selectedobra.company)
          ?.categoria ?? null;
      const categoriaExterno =
        categoriasDelEmpleado.find(
          (c) => c.empresa == selectedobra.companyDestino
        )?.categoria ?? null;

      const categoriaDefault = categorias.find(
        (c: any) => c.Categoria == manoObraCategoriaDefault
      );
      categoriaValue = categoriaInterno ?? categoriaDefault?.Categoria;
      categoriaExternoValue = categoriaExterno;
    }

    //ponemos el tipo de hora por defecto
    const availableHoras = getTipoHoras(categoriaValue, resetByUserWorker);
    const linea = lineItem;
    const hayTipoHoras = tipoHoras && tipoHoras.length > 0;

    if ((hayTipoHoras && !linea?.typeHour) || resetByUserWorker) {
      if (availableHoras.length > 1) {
        const defaultIsInAvailable = availableHoras.some(
          (th: any) => th.value === manoObraTipoHoraDefault
        );
        const typeHour = tipoHoras.find(
          (c: any) => c.TipoHora === manoObraTipoHoraDefault
        );

        allowParteFuturo = typeHour?.ParteFuturo === 'Si';
        typeHourValue = defaultIsInAvailable
          ? typeHour?.TipoHora
          : availableHoras[0].value;
      }
    } else if (hayTipoHoras && linea?.typeHour) {
      const typeHour = tipoHoras.find(
        (c: any) => c.TipoHora === +linea.typeHour
      );
      allowParteFuturo = typeHour?.ParteFuturo === 'Si';
    }

    // Si typeHourValue no está en availableHoras, asignamos el primero disponible
    if (
      availableHoras.length &&
      availableHoras.every((th: any) => th.value !== +typeHourValue)
    ) {
      typeHourValue = availableHoras[0].value;
    }

    //ponemos el articulo de la mano de obra por defecto
    if (!lineItem?.article || resetByUserWorker) {
      const { article, articleExterno } = getArticleManoObra(
        categoriaValue,
        categoriaExternoValue,
        typeHourValue
      );
      articleValue = article;
      articleExternoValue = articleExterno;
    }

    let chapter = lineItem?.chapter ?? undefined
    let activity = lineItem?.activity ?? undefined
    let subactivity = lineItem?.subactivity ?? undefined
    if (lineas?.anchorFields?.active) {
      chapter = lineas?.list?.slice()?.reverse()?.find((l: any) => !!l.chapter)?.chapter;
      activity = lineas?.list?.slice()?.reverse()?.find((l: any) => !!l.activity)?.activity;
      subactivity = lineas?.list?.slice()?.reverse()?.find((l: any) => !!l.subactivity)?.subactivity;
    }

    let extraOpcional = {};
    if (newDataSales) {
      extraOpcional = {
        ...extraOpcional,
        data_sales_pricing: newDataSales,
        data_sales_pricing_externo: newDataSalesExterno,
      };
    }

    const toUpdate = {
      hoursAndMinutesInRaw,
      category: categoriaValue,
      categoryExterno: categoriaExternoValue,
      typeHour: typeHourValue,
      precio: precioValue,
      precioExterno: precioExternoValue,
      article: articleValue,
      allowParteFuturo,
      articleExterno: articleExternoValue,
      costeEmpleado: costeEmpleadoValue,
      userWorker: userWorkerValue,
      user: null,
      minutes,
      hours,
      chapter,
      activity,
      subactivity,
      ...extraOpcional,
    };

    if (toUpdate) {
      changeLineHandler({ ...lineItem, ...toUpdate });
    }

    setLoaded(true);
  };

  useEffect(() => {
    updateData();
  }, [categorias, tipoHoras, lineas.list[props.index]?.typeHour]);

  const fetchData = async () => {
    const res: any = await reportLinesService.getReporteLinesUser(
      lineas?.list[props.index]?.userWorker?.id ?? props?.data?.usuario,
      props?.data?.id ?? null
    );
    setAllLinesUser(res?.data);
  };

  const existeSolapamiento = (
    linea: Linea,
    allLinesUserDataMO: Linea[]
  ): boolean => {
    if (!linea?.datos) return false;

    const { inicio: linea_inicio, fin: linea_finRaw, userWorker } = linea.datos;
    const madrugada = linea_inicio > linea_finRaw;
    const linea_fin = madrugada ? linea_finRaw + 86400000 : linea_finRaw;

    const userId = userWorker?.id ?? props?.data?.usuario;
    if (!userId) return false;

    const checkOverlap = (_linea: Linea): boolean => {
      const { inicio: _linea_inicio, fin: _linea_finRaw } = _linea.datos;
      const _madrugada = _linea_inicio > _linea_finRaw;
      const _linea_fin = _madrugada ? _linea_finRaw + 86400000 : _linea_finRaw;

      return _linea_inicio < linea_fin && _linea_fin > linea_inicio;
    };

    const linesWithInicioAndFinEditing =
      allLinesUserDataMO?.filter(
        (_linea) =>
          _linea.datos.inicio &&
          _linea.datos.fin &&
          (_linea.datos?.user || _linea.datos?.userWorker?.id) === userId &&
          !_linea.datos.idParte
      ) ?? [];

    const linesWithInicioAndFinInApi =
      allLinesUserDataMO?.filter(
        (_linea) =>
          _linea.datos.inicio && _linea.datos.fin && _linea.datos.idParte
      ) ?? [];

    return (
      linesWithInicioAndFinInApi.some(checkOverlap) ||
      linesWithInicioAndFinEditing.some(checkOverlap)
    );
  };

  const validaErrores = (linea: any) => {
    const allLinesUserData = allLinesUser?.data?.map((linea: any) => {
      const date = new Date(linea?.date);

      const lineParsed = reportLinesService.parseReportLine(linea, date);
      return {
        ...lineParsed,
        idLinea: linea.id,
        date_format: date.toLocaleDateString(),
      };
    });

    const date = selectedobra?.date ? new Date(selectedobra.date) : new Date();

    // Validamos si hay horas solapadas
    let solapamiento = 0;
    let solapamientoLinea = 0;
    const date_parte = date.toLocaleDateString();

    const lineParsed = reportLinesService.parseReportLine(linea, date);
    if (
      !Boolean(lineas?.list[props.index]?.hoursAndMinutesInRaw) &&
      existeSolapamiento(lineParsed, allLinesUserData)
    ) {
      solapamiento = solapamientoLinea = 1;
    }

    const madrugada = lineParsed?.datos?.inicio > lineParsed?.datos?.fin;
    let noMadrugada = madrugada && !manoObraDiferenteDia;

    const _lineas = lineas?.list
      ?.filter((_l: any) => _l?.index !== linea?.index)
      ?.map((_l: any) => {
        const lineParsed2 = reportLinesService.parseReportLine(_l, date);

        return {
          ...lineParsed2,
          date_format: date_parte,
        };
      });
    if (
      !Boolean(lineas?.list[props.index]?.hoursAndMinutesInRaw) &&
      existeSolapamiento(lineParsed, _lineas)
    ) {
      solapamiento = solapamientoLinea = 1;
    }

    // Validamos si el precio es correcto
    let incorrectPrice = 0;
    if (
      linea &&
      linea.typeHour == constants.tipo_hora_varios.TipoHora &&
      linea?.precio &&
      Array.isArray(linea.precio)
    ) {
      const hasAnyPriceError = linea.precio.some(
        (calendario: any) => calendario.precio == NO_MATCH_CATEGORY_TYPEHOUR
      );
      if (hasAnyPriceError) incorrectPrice = 1;
    } else if (linea && linea.precio == NO_MATCH_CATEGORY_TYPEHOUR) {
      incorrectPrice = 1;
    }

    // Validamos si el article es correcto
    let incorrectArticle = 0;
    const timeFill =
      Boolean(
        lineas?.list[props.index]?.endTime &&
        lineas?.list[props.index]?.startTime
      ) ||
      Boolean(
        lineas?.list[props.index]?.hours + lineas?.list[props.index]?.minutes
      );

    if (linea && linea.article == NO_MATCH_CATEGORY_TYPEHOUR && timeFill) {
      incorrectArticle = 1;
    }

    if (solapamiento || noMadrugada || incorrectPrice || incorrectArticle) {
      if (props?.setSolapamiento)
        props?.setSolapamiento({
          index: linea.index,
          value: 1,
        });
    } else {
      if (props?.setSolapamiento)
        props?.setSolapamiento({
          index: linea.index,
          value: 0,
        });
    }

    if (solapamientoLinea && props.editable) {
      return (
        <Grid item xs={12} md={12}>
          <p style={{ color: 'red' }}>
            Existen horas ya imputadas para la fecha seleccionada
          </p>
        </Grid>
      );
    }
    if (noMadrugada && props.editable) {
      return (
        <Grid item xs={12} md={12}>
          <p style={{ color: 'red' }}>
            No puede poner horas en la madrugada, crear un nuevo parte
          </p>
        </Grid>
      );
    }
    if (incorrectPrice && props.editable) {
      return (
        <Grid item xs={12} md={12}>
          <p style={{ color: 'red' }}>
            {linea.typeHour == constants.tipo_hora_varios.TipoHora
              ? 'No hay un precio disponible para algún calendario, teniendo en cuenta la categoría y el tipo de hora del calendario.'
              : 'No hay un precio disponible para la categoría y tipo de hora seleccionadas.'}
          </p>
        </Grid>
      );
    }
    if (incorrectArticle && props.editable) {
      return (
        <Grid item xs={12} md={12}>
          <p style={{ color: 'red' }}>
            No hay un articulo disponible para la categoría y tipo de hora
            seleccionadas
          </p>
        </Grid>
      );
    }
  };

  //

  const filterCalendar = ({
    c,
    linea_fin,
    linea_inicio,
    madrugada,
    hora_fin,
    hora_inicio,
    diaSemana,
    diaSemanaM,
  }: any) => {
    const horaIncluded =
      (hora_inicio < Number(c?.HoraFin) &&
        hora_fin > Number(c?.HoraInicio) &&
        c?.[diaSemana]) ||
      (madrugada &&
        ((0 < Number(c?.HoraFin) &&
          hora_fin > Number(c?.HoraInicio) &&
          c?.[diaSemanaM]) ||
          (hora_inicio < Number(c?.HoraFin) &&
            2400 > Number(c?.HoraInicio) &&
            c?.[diaSemana])));
    // console.log('horaIncluded', c,  linea_inicio, linea_fin, horaIncluded, c?.[diaSemanaM], linea_fin >= c?.inicio, linea_inicio <= c?.fin);
    return linea_fin >= c?.inicio && linea_inicio <= c?.fin && horaIncluded;
  };

  const cleanSolapados = (_calendarios: any) => {
    let _calendariosClean: any = [];
    _calendarios.map((c: any) => {
      const _calendariosClean2 = _calendariosClean.filter(
        (c2: any) =>
          Number(c2?.HoraInicio) < Number(c?.HoraFin) &&
          Number(c2?.HoraFin) > Number(c?.HoraInicio)
      );
      if (_calendariosClean2.length == 0) {
        _calendariosClean.push(c);
      }
    });
    return _calendariosClean;
  };

  const obtenerTipoHora = (lineaA: any, dateMod: any) => {
    const date = selectedobra?.date ? new Date(selectedobra.date) : new Date();

    const linea: any = reportLinesService.parseReportLine(
      { ...lineaA, ...dateMod },
      date
    );
    // Validamos que calendarios de config estè en true;
    if (!config_calendarios?.calendario) return linea?.datos?.typeHour;
    if (
      !linea?.datos?.inicio ||
      !linea?.datos?.fin ||
      !linea?.datos?.endTime ||
      !linea?.datos?.startTime
    ) {
      return linea?.datos?.typeHour;
    }

    let _calendarios = [];
    // Validamos config para usar el calendario correcto
    const referencia_obra = selectedobra?.calRef;
    const linea_inicio = linea?.datos?.inicio;
    const hora_inicio = Number(linea?.datos?.startTime.replace(':', ''));
    const hora_fin = Number(linea?.datos?.endTime.replace(':', ''));
    let linea_fin = linea?.datos?.fin;
    const madrugada = linea_inicio > linea_fin;
    linea_fin = madrugada ? linea_fin + 86400000 : linea_fin;
    //TODO: revisar las que son de madrugada que dia de la semana son
    const days = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
    const diaK = new Date(linea_inicio).getDay();
    const diaSemana = days[diaK];
    const diaSemanaM = days[diaK + 1];

    if (config_calendarios.referencia_obra) {
      // Antes que cualquier cosa validamos que no sea una Obra = 1000 (  Informar tipos de horas especiales )
      // Si el capítulo seleccionado no tiene referencia seguimos con el proceso actual
      if (
        selectedobra?.tipo_obra == 1000 &&
        linea?.datos?.chapter?.Referencia
      ) {
        const _tipo_hora = tipoHoras.find(
          (t: any) => t.TipoHora == linea?.datos?.chapter?.Referencia
        );
        if (!_tipo_hora) alert('Tipo hora no encontrada');
        return _tipo_hora.TipoHora;
      }

      _calendarios = calendarios
        .filter((c: any) => c?.Codigo == referencia_obra)
        .filter((c: any) =>
          filterCalendar({
            c,
            linea_fin,
            linea_inicio,
            madrugada,
            hora_fin,
            hora_inicio,
            referencia_obra,
            diaSemana,
            diaSemanaM,
          })
        )
        .sort((a: any, b: any) => b?.Orden - a?.Orden);
    }
    // console.log('_calendarios', _calendarios);

    if (_calendarios?.length === 0) {
      // Usamos calendario por defecto
      _calendarios = calendarios
        .filter((c: any) => c?.Codigo == config_calendarios.calendario_defecto)
        .filter((c: any) =>
          filterCalendar({
            c,
            linea_fin,
            linea_inicio,
            madrugada,
            hora_fin,
            hora_inicio,
            referencia_obra,
            diaSemana,
            diaSemanaM,
          })
        )
        .sort((a: any, b: any) => b?.Orden - a?.Orden);
    }

    if (_calendarios?.length == 0) {
      alert('Ningun calendario encontrado');
      return linea?.datos?.typeHour;
    }

    // console.log('_calendarios', _calendarios);
    _calendarios = cleanSolapados(_calendarios);
    // console.log('_calendarios', _calendarios);

    if (_calendarios?.length > 1) {
      setCalendariosEnTipoDeHoraVarios(_calendarios);
      return constants.tipo_hora_varios.TipoHora;
    }

    const tipoHoraMax = _calendarios[0]?.TipoHora;
    const _tipo_hora = tipoHoras.find(
      (t: any) => t.TipoHora == tipoHoraMax
    )?.NombreTipoHora;
    if (!_tipo_hora && _calendarios?.length) alert('Tipo hora no encontrada');

    return tipoHoraMax;
  };

  let startTime = lineas?.list[props.index]?.startTime;

  const getEndHour = (number?: string) => {
    const numberHours = number
      ? number
      : lineas?.list[props.index]?.numberHours;

    if (!startTime || !numberHours) {
      return;
    }

    const startSecs = calculateSeconds(
      startTime.replace('null', '00').split(':')
    );

    const nHoursSeconds = calculateSeconds(
      numberHours.replace('null', '00').split(':')
    );

    return calculateTime(startSecs + nHoursSeconds);
  };

  const getHours = (hour?: string) => {
    const endTime = hour ? hour : lineas?.list[props.index]?.endTime;
    if (!startTime || !endTime) {
      return;
    }

    const startSecs = calculateSeconds(
      startTime.replace('null', '00').split(':')
    );
    const endSecs = calculateSeconds(endTime.replace('null', '00').split(':'));

    const difference = Math.abs(startSecs - endSecs);

    let totalHour = calculateTime(difference);
    if (endSecs < startSecs) {
      const x = calculateTime(difference).split(':');
      totalHour = `${24 - Number(x[0])}:${x[1]}`;
    }

    return totalHour;
  };

  const recalculate = (hour: string, changeInitial: boolean) => {
    startTime = hour;

    let dates = {
      startTime: startTime,
      endTime: changeInitial ? undefined : lineas.list[props.index]?.endTime,
      numberHours: changeInitial
        ? undefined
        : lineas.list[props.index]?.numberHours,
    };
    if (getHorasFinDisabled() && !changeInitial)
      dates = { ...dates, endTime: getEndHour() };
    if (getNumberHoursDisabled()) dates = { ...dates, numberHours: getHours() };

    const newTypeHour = obtenerTipoHora(lineas.list[props.index], dates);

    changeLineHandler({
      ...lineas.list[props.index],
      ...dates,
      typeHour: newTypeHour,
      user: lineas?.list[props.index]?.userWorker?.id,
    });
  };

  const activitiesOptions = useMemo<EsfirusSelectPropsOption[]>(
    () =>
      (activities || []).map((act: Activity) => ({
        ...act,
        label: act.description,
        value: act.activity_code,
      })),
    [activities] // Dependencia: recalcula cuando cambian las actividades
  );

  const subActivitiesOptions = useMemo(() => {
    const activity = lineas.list[props.index]?.activity;

    return activity
      ? (subActivities || []).reduce(
        (acc: EsfirusSelectPropsOption[], act: SubActivity) => {
          if (act.activity_code === activity) {
            acc.push({
              ...act,
              label: act.description,
              value: act.subactivity_code,
            });
          }
          return acc;
        },
        []
      )
      : [];
  }, [
    subActivities,
    lineas,
    props.index,
  ]);

  const getChapterOptions = (): any[] => {
    if (!selectedobra.capitulosByPresupuesto) {
      return [];
    }
    return selectedobra.capitulosByPresupuesto?.map((cap: Capitulo) => ({
      ...cap,
      label: cap.label,
      value: cap.Capitulo,
    }));
  };

  const categoriasOptions = useMemo(() => {
    return categorias.map((cat: Categoria) => ({
      value: cat.Categoria,
      label: cat.NombreCategoria,
    }));
  }, [categorias]);

  const getTipoHoras = (
    category = null,
    userWorker: UserWithDataParsed | null = null
  ) => {
    const categoriaSelected = category ?? lineas.list[props.index]?.category;
    const userWorkerSelected =
      userWorker ??
      (lineas?.list[props.index]?.userWorker as UserWithDataParsed);
    let resultTipoHoras = tipoHoras;

    if (
      tipoHoras &&
      tipoHoras.length &&
      manoObrasList &&
      manoObrasList.length &&
      (categoriaSelected || categoriaSelected == 0)
    ) {
      // Primero filtro de tabla tipoHorasByEmpleado.
      let flagByEmpleado = false;
      if (
        tipoHorasByEmpleado &&
        tipoHorasByEmpleado.length &&
        userWorkerSelected
      ) {
        const companies_employees = [
          ...userWorkerSelected.data.internal,
          ...userWorkerSelected.data.external,
        ];
        const empleadoFiltro = tipoHorasByEmpleado.filter(
          (th: any) =>
            th.Empresa == selectedobra.company &&
            th.Empleado ==
            companies_employees.find(
              (ce) => ce.empresa == selectedobra.company
            )?.empleado
        );
        if (empleadoFiltro.length > 0) {
          const empleadoOpciones = empleadoFiltro.map(
            (th: any) => th.Tipo_Hora
          );
          resultTipoHoras = tipoHoras.filter((th: any) =>
            empleadoOpciones.includes(th.TipoHora)
          );
          flagByEmpleado = true;
        }
      }

      // Si no entramos en el primero, filtramos por categoría en mano de obra.
      if (!flagByEmpleado) {
        const manoObrasByCategorySelected = manoObrasList.filter(
          (manoObra: any) => manoObra.Categoria == categoriaSelected
        );
        const tiposDeHorasParaSeleccionar = manoObrasByCategorySelected.map(
          (manoObra: any) => manoObra.Tipo_Hora
        );
        resultTipoHoras = tipoHoras.filter((hora: any) =>
          tiposDeHorasParaSeleccionar.includes(hora.TipoHora)
        );
      }
    }

    resultTipoHoras = [...resultTipoHoras, constants.tipo_hora_varios];
    return resultTipoHoras.map((tipHora: TipoHora) => ({
      value: tipHora.TipoHora,
      label: tipHora.NombreTipoHora,
      disabled: tipHora.TipoHora === 1000,
    }));
  };

  const getHorasFinDisabled = () => {
    if (!props.editable) {
      return true;
    }
    return manoObra !== 'hora';
  };

  const getNumberHoursDisabled = () => {
    if (!props.editable) {
      return true;
    }
    return manoObra !== 'cantidad';
  };

  const getCategoryDisabled = () => {
    if (!props.editable) {
      return true;
    }
    return !manoObraCategoria;
  };

  const getTipoHorasDisabled = () => {
    if (
      loadingSP ||
      !props.editable ||
      lineas.list[props.index]?.typeHour == constants.tipo_hora_varios.TipoHora
    ) {
      return true;
    }
    return !manoObraTipoHora;
  };

  const getHoursDecimal = (linea?: any) => {
    const date = selectedobra?.date ? new Date(selectedobra.date) : new Date();
    const line = linea ?? lineas.list[props.index];
    const lineParsed = reportLinesService.parseReportLine(line, date);
    return lineParsed?.horas ? Number(lineParsed?.horas) : 0;
  };

  return {
    activitiesOptions,
    allowToSelectEmpleado,
    categorias,
    categoriasOptions,
    changeLineHandler,
    descManoObra,
    empleadosOptions,
    getCategoryDisabled,
    getChapterOptions,
    getEndHour,
    getHorasFinDisabled,
    getHours,
    getNumberHoursDisabled,
    getTipoHoras,
    getTipoHorasDisabled,
    lineas,
    obtenerTipoHora,
    recalculate,
    selectedobra,
    showManoObraCategoria,
    showManoObraTipoHora,
    subActivitiesOptions,
    updateData,
    users,
    validaErrores,
    handleBlurTimeInputs,
    description,
    setDescription,
  };
};

export { useLaborLinesHook };
